// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Dashboard overview health-summary + finding-review-card cluster:
// - healthCardKeysForSummary: which monitor cards to show for the
//   current summary scope.
// - reviewCardSortItems: orders the cards (cylinder-focus mode +
//   engine review queue + remaining cards).
// - renderHealthSummary: the big overview renderer that updates the
//   overall score header, hides/shows turbo/carb cards, refreshes per-
//   monitor score chips, dispatches operating-context cards, and then
//   hands off to renderFindingReviewCards.
// - reviewCardStateKey + reviewCardDetailsHtml + reviewCardHtml +
//   findingBodyHtml: the finding-review card markup.
// - renderFindingReviewCards: builds the review-card collection.
// - scrollReviewCardIntoView: scroll-into-view glue.

import { engineInfoForSourceNumber } from "../core/engines.js";
import { maintenanceContextFlightDay } from "../core/maintenance-context.js";

export function createHealthSummaryRenderer({
  escapeHtml,
  hasCarbTempChannel,
  findingDisplayClass,
  findingDisplayLabel,
  findingMetaItems,
  findingMetaMarkup,
  findingCausePresentation,
  metricDisplayClass,
  scoreMarkup,
  reviewMonitorEnabled,
  healthSummaryForCurrentEngineScope,
  renderSensitivityControl,
  renderScopeSwitch,
  renderMultiEngineOverview,
  renderCylinderScopeSummary,
  renderOverviewDetectorVisual,
  renderOperatingContextCards,
  selectedOperatingContextReviewCardHtml,
  updateHealthCardSelection,
  scheduleOverviewReviewPanelHeight,
  currentEngineDescriptors,
  engineReviewItems,
  engineForSummaryScope,
  metricMatchesEngineScope,
  scopedSummaryFinding,
  findingForMetric,
  cylinderMetricSummary,
  cylinderFinding,
  healthCardNoteMarkup,
  openTrendFlight,
  appReadOnlyMode = () => false,
  getSelectedFlight,
  getSelectedHealthKey,
  getOverviewSelectionCleared = () => false,
  setSelectedHealthKey,
  getSelectedCylinderNumber,
  getSelectedEngineScope,
  setRenderedHealthSummary,
  reviewNoteSectionHtml,
  saveReviewNote,
  renderMaintenanceContext = null,
}) {
  const chtUnavailableScoreTileNotes = Object.freeze({
    cht: ["No CHT channel data"],
    cooling: ["Need multiple CHT channels", "No comparable CHT channels"],
    coolingRate: ["No CHT channel data"],
    abnormalCombustion: ["Need per-cylinder CHT and EGT channels"],
    coolantMargin: ["No coolant temperature channel"],
  });
  const expandedReviewKeys = new Set();
  // Last markup written per health-card key (score/class/note), so re-renders
  // can skip identical writes. Keyed by card key only: the markup strings
  // themselves already encode flight/scope/sensitivity differences.
  const healthCardMarkupCache = new Map();
  // The exact innerHTML the finding panel last rendered. A rebuild is skipped
  // when the regenerated markup is byte-identical, which keeps the existing
  // DOM nodes, their listeners, and the user's scroll position. In-place
  // mutations (the expand/collapse toggle) are safe: they make the NEXT
  // render's markup differ from this string, forcing a real rebuild.
  let lastFindingPanelHtml = null;

  function replaceFindingPanelHtml(panel, html) {
    panel.className = "panel finding-panel is-single-review";
    if (html === lastFindingPanelHtml && panel.firstElementChild) return false;
    panel.innerHTML = html;
    lastFindingPanelHtml = html;
    return true;
  }

  function metricAppliesToScoreTile(key, metric) {
    if (metric?.details?.status === "not-applicable") return false;
    const unavailableNotes = chtUnavailableScoreTileNotes[key] ?? [];
    return !unavailableNotes.includes(metric?.note);
  }

  function healthCardKeysForSummary(summary) {
    const keys = ["sensors", "oilSystem", "coolantMargin", "cht", "egt", "valve", "abnormalCombustion", "inductionLeak", "cooling", "coolingRate", "charging", "electrical"];
    if (summary?.turbo?.details?.available) keys.splice(2, 0, "turbo");
    if (hasCarbTempChannel(summary)) keys.splice(keys.indexOf("cooling"), 0, "carbIce");
    // Opt-in metric: only show its card when the detector actually evaluated a
    // configured per-engine target (otherwise it would show a "no data" card on
    // every aircraft). Mirrors the conditional turbo/carbIce cards.
    if (summary?.takeoffEnrichment?.details?.status === "evaluated") keys.splice(keys.indexOf("cht"), 0, "takeoffEnrichment");
    const engines = currentEngineDescriptors();
    const selectedHealthKey = getSelectedHealthKey();
    const selectedEngineScope = getSelectedEngineScope();
    if (engines.length > 1 && summary?.engineDelta && !getSelectedCylinderNumber() && (selectedEngineScope === "aircraft" || selectedEngineScope === "compare" || selectedHealthKey === "engineDelta")) {
      keys.push("engineDelta");
    }
    return keys.filter((key) => summary?.[key] && metricAppliesToScoreTile(key, summary[key]) && reviewMonitorEnabled(key, getSelectedFlight()?.aircraft));
  }

  function healthCardTitleForKey(key, summary) {
    if (key === "valve" && summary?.applicability?.engineFamily === "rotax") return "EGT Stability";
    return {
      sensors: "Sensors",
      turbo: "Turbo/Boost",
      oilSystem: "Oil System",
      coolantMargin: "Coolant Margin",
      takeoffEnrichment: "Takeoff Enrichment",
      cht: "CHT Heat",
      egt: "EGT Balance",
      valve: "EGT Rhythm",
      abnormalCombustion: "Abnormal Combustion",
      inductionLeak: "Induction Leak",
      cooling: "Cooling",
      coolingRate: "Cooling Rate",
      carbIce: "Carb Ice",
      charging: "Start Charge",
      electrical: "Electrical",
      engineDelta: "Engine Delta",
    }[key] ?? key;
  }

  function reviewCardSortItems(baseSummary, displaySummary) {
    const selectedCylinderNumber = getSelectedCylinderNumber();
    if (selectedCylinderNumber) {
      const metric = cylinderMetricSummary(selectedCylinderNumber, baseSummary);
      const metricKeys = healthCardKeysForSummary(baseSummary)
        .filter((key) => key !== "engineDelta");
      const items = [
        {
          key: "cylinder",
          metric: { score: metric.score, className: metric.className, note: metric.probeNote },
          finding: cylinderFinding(selectedCylinderNumber, baseSummary),
          className: metric.className,
        },
        ...metricKeys.map((key) => ({
          key,
          metric: baseSummary[key],
          finding: findingForMetric(key, baseSummary),
          className: findingDisplayClass(findingForMetric(key, baseSummary), baseSummary[key]),
        })),
      ];
      return items;
    }

    const reviewItems = engineReviewItems(displaySummary)
      .filter((item) => item?.key && item.key !== "mag" && reviewMonitorEnabled(item.key, getSelectedFlight()?.aircraft));
    const reviewKeys = new Set(reviewItems.map((item) => item.key));
    const remaining = healthCardKeysForSummary(displaySummary)
      .filter((key) => !reviewKeys.has(key))
      .map((key) => {
        const metric = displaySummary[key];
        const finding = findingForMetric(key, displaySummary);
        return {
          key,
          metric,
          finding,
          className: findingDisplayClass(finding, metric),
        };
      });
    return [
      ...reviewItems.map((item) => ({
        ...item,
        finding: item.finding ?? findingForMetric(item.key, displaySummary),
        className: item.className ?? findingDisplayClass(item.finding ?? findingForMetric(item.key, displaySummary), item.metric),
      })),
      ...remaining,
    ];
  }

  function renderHealthSummary(summary) {
    if (!summary) return;
    const selectedCylinderNumber = getSelectedCylinderNumber();
    const displaySummary = selectedCylinderNumber ? summary : healthSummaryForCurrentEngineScope(summary);
    setRenderedHealthSummary(displaySummary);
    renderSensitivityControl(displaySummary);
    renderScopeSwitch();
    renderMultiEngineOverview(summary);
    renderCylinderScopeSummary(summary);
    renderOverviewDetectorVisual?.(displaySummary);
    const engineBand = document.querySelector(".summary-band.overview-only:not(#cylinderSummaryBand)");
    if (engineBand) {
      engineBand.dataset.healthScope = displaySummary.scope?.engineId ?? "aircraft";
    }
    const overallTitle = document.querySelector("#overallScoreTitle");
    if (overallTitle) {
      overallTitle.textContent = displaySummary.scope?.label ?? "Overall";
    }
    const turboAvailable = Boolean(displaySummary.turbo?.details?.available);
    const carbIceAvailable = hasCarbTempChannel(displaySummary);
    document.querySelectorAll('[data-health-card="turbo"]').forEach((card) => {
      card.hidden = !turboAvailable;
      if (!turboAvailable) card.classList.remove("is-selected");
    });
    document.querySelectorAll('[data-health-card="carbIce"]').forEach((card) => {
      card.hidden = !carbIceAvailable;
      if (!carbIceAvailable) card.classList.remove("is-selected");
    });
    const selectedFlight = getSelectedFlight();
    const compatibilityBanner = document.querySelector("#analysisCompatibilityBanner");
    if (compatibilityBanner) {
      compatibilityBanner.hidden = true;
      compatibilityBanner.classList?.remove("is-analysis-update");
      compatibilityBanner.innerHTML = "";
    }
    const visibleHealthCardKeys = new Set(healthCardKeysForSummary(displaySummary));
    let selectedHealthKey = getSelectedHealthKey();
    if (selectedHealthKey && !visibleHealthCardKeys.has(selectedHealthKey)) {
      setSelectedHealthKey(null);
      selectedHealthKey = null;
    }
    if (!turboAvailable && selectedHealthKey === "turbo") {
      setSelectedHealthKey(null);
      selectedHealthKey = null;
    }
    if (!carbIceAvailable && selectedHealthKey === "carbIce") {
      setSelectedHealthKey(null);
      selectedHealthKey = null;
    }
    if (selectedHealthKey && !reviewMonitorEnabled(selectedHealthKey, selectedFlight?.aircraft)) {
      setSelectedHealthKey(null);
      selectedHealthKey = null;
    }
    const scopeEngine = engineForSummaryScope(displaySummary);
    if (scopeEngine && selectedHealthKey && !metricMatchesEngineScope(selectedHealthKey, displaySummary[selectedHealthKey], scopeEngine)) {
      setSelectedHealthKey(null);
      selectedHealthKey = null;
    }
    const selectionCleared = getOverviewSelectionCleared();
    const activeFindingMetric = selectedHealthKey
      ? displaySummary[selectedHealthKey]
      : selectionCleared ? null : displaySummary.overall;
    const overallValue = document.querySelector("#overallScoreValue");
    const overallLabel = document.querySelector("#overallScoreLabel");
    if (overallValue) {
      overallValue.textContent = Number.isFinite(displaySummary.overall.score) ? String(displaySummary.overall.score) : "--";
      overallValue.className = displaySummary.overall.className;
      overallValue.title = displaySummary.overall.note ?? "";
    }
    if (overallLabel) {
      overallLabel.textContent = displaySummary.overall.label;
      overallLabel.className = displaySummary.overall.className;
      overallLabel.title = displaySummary.overall.note ?? "";
    }
    const healthCardKeys = [...visibleHealthCardKeys].filter((key) => key !== "engineDelta");
    document.querySelectorAll("[data-health-card]").forEach((card) => {
      const key = card.dataset.healthCard;
      if (!key || key === "overall") return;
      const available = (key !== "turbo" || turboAvailable) && (key !== "carbIce" || carbIceAvailable);
      const enabled = reviewMonitorEnabled(key, selectedFlight?.aircraft);
      card.hidden = !visibleHealthCardKeys.has(key) || !available || !enabled;
      if (card.hidden) {
        card.classList.remove("is-selected");
        card.setAttribute("aria-pressed", "false");
      }
    });
    healthCardKeys.forEach((key) => {
      const metric = displaySummary[key];
      const titleElement = document.querySelector(`[data-health-title="${key}"]`);
      const scoreElement = document.querySelector(`[data-health-score="${key}"]`);
      const noteElement = document.querySelector(`[data-health-note="${key}"]`);
      // Diff against the last markup this renderer wrote: most re-renders
      // (tile taps, cylinder focus, sensitivity nudges) change only a few
      // tiles, and skipping identical innerHTML writes avoids re-parsing and
      // re-layouting every card on every pass.
      const cached = healthCardMarkupCache.get(key) ?? {};
      const next = {
        title: healthCardTitleForKey(key, displaySummary),
        score: scoreMarkup(metric),
        scoreClass: metricDisplayClass(key, metric),
        note: healthCardNoteMarkup(key, metric, { summary: displaySummary }),
      };
      if (titleElement && titleElement.textContent !== next.title) titleElement.textContent = next.title;
      if (scoreElement) {
        if (next.score !== cached.score) scoreElement.innerHTML = next.score;
        if (next.scoreClass !== cached.scoreClass) scoreElement.className = next.scoreClass;
      }
      if (noteElement && next.note !== cached.note) noteElement.innerHTML = next.note;
      healthCardMarkupCache.set(key, next);
    });
    renderOperatingContextCards(displaySummary);
    updateHealthCardSelection();
    renderFindingReviewCards(
      summary,
      displaySummary,
      selectedHealthKey ? findingForMetric(selectedHealthKey, displaySummary) : selectionCleared ? null : scopedSummaryFinding(displaySummary),
      activeFindingMetric,
    );
    scheduleOverviewReviewPanelHeight();
  }

  function findingBodyHtml(bodyText = "") {
    if (bodyText.includes("\n")) {
      return bodyText.split(/\n{2,}/)
        .map((segment) => escapeHtml(segment).replace(/\n/g, "<br>"))
        .join("<br><br>");
    }
    return escapeHtml(bodyText);
  }

  function reviewCardStateKey(key) {
    return [getSelectedFlight()?.id ?? "", getSelectedEngineScope() ?? "", getSelectedCylinderNumber() ?? "", key].join("|");
  }

  function reviewCardDetailsHtml(finding, metric) {
    const sections = finding.summarySections ?? [];
    const evidenceRows = finding.evidence ?? [];
    const references = finding.references ?? [];
    const causeRows = finding.causes ?? [];
    const causePresentation = findingCausePresentation(finding, metric);
    return `
      ${sections.length ? `
        <div class="finding-summary-sections">
          ${sections.map((section) => `
            <section>
              <h4>${escapeHtml(section.title)}</h4>
              <ul>
                ${section.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}
              </ul>
            </section>
          `).join("")}
        </div>
      ` : ""}
      ${evidenceRows.length ? `
        <div class="finding-evidence-block">
          <h4>${escapeHtml(finding.evidenceTitle ?? "Evidence used")}</h4>
          <dl>
            ${evidenceRows.map((row) => {
              const rowClass = ["green", "amber", "red"].includes(row.className) ? ` class="${row.className}"` : "";
              return `
                <div>
                  <dt${rowClass}>${escapeHtml(row.label)}</dt>
                  <dd>
                    <strong>${escapeHtml(row.value)}</strong>
                    <span>${escapeHtml(row.detail)}</span>
                  </dd>
                </div>
              `;
            }).join("")}
          </dl>
        </div>
      ` : ""}
      ${references.length ? `
        <div class="finding-references-block">
          <h4>Evidence flights for this aircraft</h4>
          <ul class="evidence-flight-list">
            ${references.map((reference) => `
              <li>
                <button class="evidence-flight-button" data-evidence-flight-id="${escapeHtml(reference.id)}" type="button"${reference.isCurrent ? " disabled" : ""}>
                  <span>
                    <strong>${escapeHtml(reference.title)}${reference.isCurrent ? " (selected)" : ""}</strong>
                    ${escapeHtml(reference.note)}
                  </span>
                  <em>${reference.score <= 50 ? "Repeat" : "Watch"}</em>
                </button>
              </li>
            `).join("")}
          </ul>
        </div>
      ` : ""}
      ${causeRows.length ? `
        <div class="finding-causes-block">
          <h4>${escapeHtml(causePresentation.title)}</h4>
          <ul class="cause-list${causePresentation.weighted ? "" : " is-plain"}">
            ${causeRows.map(([label, value]) => causePresentation.weighted
              ? `<li><span>${escapeHtml(label)}</span><meter value="${value}">${Math.round(value * 100)}%</meter><b>${Math.round(value * 100)}%</b></li>`
              : `<li><span>${escapeHtml(label)}</span></li>`).join("")}
          </ul>
        </div>
      ` : ""}
    `;
  }

  function reviewCardHtml(item, index) {
    const finding = item.finding ?? {};
    const metric = item.metric ?? {};
    const key = item.key ?? `review-${index}`;
    const className = findingDisplayClass(finding, metric);
    const label = findingDisplayLabel(finding, metric);
    const selectedHealthKey = getSelectedHealthKey();
    const selected = selectedHealthKey ? key === selectedHealthKey : index === 0;
    const expanded = expandedReviewKeys.has(reviewCardStateKey(key));
    const collapsibleClass = expanded ? "is-review-expanded" : "is-review-collapsed";
    const titleId = selected ? ' id="findingTitle"' : "";
    const statusId = selected ? ' id="findingStatus"' : "";
    return `
      <article class="finding-review-card status-${className} ${selected ? "is-selected" : ""} ${collapsibleClass}" data-review-card-key="${escapeHtml(key)}">
        <div class="finding-panel-header">
          <p class="eyebrow">Review Context</p>
          <span${statusId} class="finding-status ${className}">${escapeHtml(label)}</span>
        </div>
        <div class="finding">
          <h3${titleId}>${escapeHtml(finding.title ?? "Engine review")}</h3>
          <p class="muted finding-meta ${findingMetaItems(finding, metric?.note ?? "").length ? "is-list" : ""}">${findingMetaMarkup(finding, metric?.note ?? "")}</p>
          <p class="finding-body">${findingBodyHtml(finding.body ?? "")}</p>
        </div>
        <div class="finding-review-details">
          ${reviewCardDetailsHtml(finding, metric)}
        </div>
        <button class="finding-disclosure-toggle" data-review-toggle="${escapeHtml(key)}" type="button" aria-expanded="${expanded ? "true" : "false"}">
          <span>${expanded ? "Hide full review" : "Show full review"}</span>
        </button>
        ${typeof reviewNoteSectionHtml === "function" ? reviewNoteSectionHtml(key) : ""}
        ${renderMaintenanceContext ? '<div data-finding-maintenance-context hidden></div>' : ""}
      </article>
    `;
  }

  // Wire the per-card note editor (present in both scoring and operating-
  // context review cards). A successful blur moves the note into a compact,
  // read-only card state; save failures leave the editor and its text intact.
  function attachReviewNoteListeners(panel) {
    if (typeof saveReviewNote !== "function") return;
    panel.querySelectorAll("[data-review-note-input]").forEach((input) => {
      const key = input.dataset.reviewNoteInput;
      const section = input.closest("[data-review-note]");
      const counter = panel.querySelector(`[data-review-note-count="${CSS.escape(String(key))}"]`);
      const saveState = panel.querySelector(`[data-review-note-save-state="${CSS.escape(String(key))}"]`);
      const saved = panel.querySelector(`[data-review-note-saved="${CSS.escape(String(key))}"]`);
      const editor = panel.querySelector(`[data-review-note-editor="${CSS.escape(String(key))}"]`);
      const body = panel.querySelector(`[data-review-note-body="${CSS.escape(String(key))}"]`);
      const editButton = panel.querySelector(`[data-review-note-edit="${CSS.escape(String(key))}"]`);

      const showSavedNote = (text) => {
        const normalized = String(text ?? "").trim();
        if (body) body.textContent = normalized;
        if (saved) saved.hidden = !normalized;
        if (editor) editor.hidden = Boolean(normalized);
        section?.classList.toggle("has-saved-note", Boolean(normalized));
        if (section) section.dataset.reviewNoteMode = normalized ? "saved" : "editing";
        if (saveState) saveState.textContent = normalized ? "" : "Note removed";
      };

      input.addEventListener("input", () => {
        if (counter) counter.textContent = String(input.maxLength - input.value.length);
        if (saveState) saveState.textContent = "";
      });
      input.addEventListener("blur", async () => {
        const submittedValue = input.value;
        if (saveState) saveState.textContent = "Saving…";
        const result = await saveReviewNote(key, submittedValue);
        if (input.value !== submittedValue) {
          if (saveState) saveState.textContent = "Unsaved changes";
          return;
        }
        if (!result?.ok) {
          if (saveState) saveState.textContent = "Not saved — try again";
          return;
        }
        input.value = String(result.body ?? "");
        if (counter) counter.textContent = String(input.maxLength - input.value.length);
        showSavedNote(input.value);
      });
      editButton?.addEventListener("click", () => {
        if (saved) saved.hidden = true;
        if (editor) editor.hidden = false;
        if (section) section.dataset.reviewNoteMode = "editing";
        if (saveState) saveState.textContent = "";
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      });
    });
  }

  function renderFindingReviewCards(baseSummary, displaySummary, fallbackFinding, fallbackMetric) {
    const panel = document.querySelector("#findingPanel");
    if (!panel) return;
    const operatingContextHtml = typeof selectedOperatingContextReviewCardHtml === "function"
      ? selectedOperatingContextReviewCardHtml(displaySummary)
      : "";
    if (operatingContextHtml) {
      renderMaintenanceContext?.(null, null);
      panel.hidden = false;
      const replacedContext = replaceFindingPanelHtml(panel, `
        <div class="finding-review-list" id="findingReviewList">
          ${operatingContextHtml}
        </div>
      `);
      if (replacedContext) attachReviewNoteListeners(panel);
      return;
    }
    if (getOverviewSelectionCleared()) {
      renderMaintenanceContext?.(null, null);
      panel.hidden = true;
      replaceFindingPanelHtml(panel, "");
      return;
    }
    panel.hidden = false;
    const items = reviewCardSortItems(baseSummary, displaySummary);
    const selectedHealthKey = getSelectedHealthKey();
    const selectedCard = selectedHealthKey
      ? items.find((item) => item.key === selectedHealthKey)
      : { key: "overall", metric: fallbackMetric ?? displaySummary?.overall, finding: fallbackFinding ?? scopedSummaryFinding(displaySummary) };
    const cards = [selectedCard ?? { key: "overall", metric: fallbackMetric ?? displaySummary?.overall, finding: fallbackFinding ?? scopedSummaryFinding(displaySummary) }];
    const replaced = replaceFindingPanelHtml(panel, `
      <div class="finding-review-list" id="findingReviewList">
        ${cards.map((item, index) => reviewCardHtml(item, index)).join("")}
      </div>
    `);
    if (renderMaintenanceContext) {
      const day = maintenanceContextFlightDay(getSelectedFlight());
      const identity = getSelectedCylinderNumber() ? engineInfoForSourceNumber(getSelectedCylinderNumber(), currentEngineDescriptors()) : null;
      renderMaintenanceContext(panel.querySelector("[data-finding-maintenance-context]"), {
        aircraft: getSelectedFlight()?.aircraft ?? "",
        metricKey: cards[0].key,
        engineId: identity?.engine?.id ?? getSelectedEngineScope(),
        cylinder: identity?.cylinder?.number ?? null,
        anchors: day ? [{ day, label: "Selected flight" }] : [],
      });
    }
    // Listeners survive a skipped rebuild (same DOM nodes); re-attach only
    // when the panel content was actually replaced.
    if (!replaced) return;
    panel.querySelectorAll("[data-review-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        const key = button.dataset.reviewToggle;
        const card = button.closest("[data-review-card-key]");
        if (!key || !card) return;
        const stateKey = reviewCardStateKey(key);
        const expanded = !expandedReviewKeys.has(stateKey);
        if (expanded) expandedReviewKeys.add(stateKey);
        else expandedReviewKeys.delete(stateKey);
        card.classList.toggle("is-review-expanded", expanded);
        card.classList.toggle("is-review-collapsed", !expanded);
        button.setAttribute("aria-expanded", expanded ? "true" : "false");
        button.querySelector("span").textContent = expanded ? "Hide full review" : "Show full review";
      });
    });
    panel.querySelectorAll("[data-evidence-flight-id]").forEach((button) => {
      button.addEventListener("click", () => openTrendFlight(button.dataset.evidenceFlightId));
    });
    attachReviewNoteListeners(panel);
  }

  function scrollReviewCardIntoView(key) {
    if (!key) return;
    const card = document.querySelector(`#findingReviewList [data-review-card-key="${CSS.escape(String(key))}"]`);
    if (card) card.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  return {
    healthCardKeysForSummary,
    healthCardTitleForKey,
    reviewCardSortItems,
    renderHealthSummary,
    findingBodyHtml,
    reviewCardStateKey,
    reviewCardDetailsHtml,
    reviewCardHtml,
    renderFindingReviewCards,
    scrollReviewCardIntoView,
  };
}
